import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CreditCardIcon from '@mui/icons-material/CreditCardOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { updateSubscriptionFields } from '../services/subscriptionService';
import { useOrganization } from '../contexts/OrganizationContext';
import {
    Box,
    Tabs,
    Tab,
    Typography,
    Button,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Menu,
    MenuItem
} from '@mui/material';

const Billing = () => {
    const navigate = useNavigate();
    const [value, setValue] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const { subscriptions, refreshSubscriptions, setSubscriptions } = useOrganization();
    const [error, setError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeSubscription, setActiveSubscription] = useState(null); // Tracks the active subscription for the menu

    const accountGuid = localStorage.getItem('authSession')
        ? JSON.parse(localStorage.getItem('authSession')).accountGuid
        : null;

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleAddCard = () => {
        console.log('Add Card button clicked!');
        // Add logic to handle adding a new card
    };

    const handleAddSubscription = (subscription = null) => {
        if (subscription) {
            // Navigate to the Manage Subscription page with the subscription details
            navigate('/manage-subscription', { state: { subscription } });
        } else {
            // Navigate for creating a new subscription
            navigate('/manage-subscription');
        }
    };

    const handleMenuOpen = (event, subscriptionGuid) => {
        setAnchorEl(event.currentTarget);
        setActiveSubscription(subscriptionGuid);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setActiveSubscription(null);
    };


    useEffect(() => {
        const loadSubscriptions = async () => {
            try {
                await refreshSubscriptions();
            } catch (err) {
                setError('Failed to load subscriptions.');
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        loadSubscriptions();
    }, []); // refreshSubscriptions


    const formatDate = (dateString) => {
        if (!dateString) return 'Never expires';
        const date = new Date(dateString);
        return isNaN(date.getTime())
            ? 'Invalid Date'
            : date.toLocaleDateString(undefined, {
                weekday: 'short', // e.g., Mon
                year: 'numeric',  // e.g., 2024
                month: 'short',    // e.g., Jan
                day: 'numeric',   // e.g., 1
            });
    };

    const handleRequestToCancel = async () => {
        if (!activeSubscription) return;

        const confirmCancel = window.confirm(
            'Are you sure you want to cancel your subscription? This action cannot be undone.'
        );

        if (!confirmCancel) {
            handleMenuClose();
            return;
        }

        const now = new Date().toISOString();
        const updatedFields = {
            canceledAt: now,
            isActive: false,
            subscriptionStatus: 'canceled',
            autoRenewStatus: false,
            assignedUsers: {
                action: 'clear',
                users: [],
            },
            expiresAt: null,
            updatedAt: now,
        };

        try {
            await updateSubscriptionFields(activeSubscription, updatedFields);
            alert('Your subscription has been successfully canceled.');
            await refreshSubscriptions();
        } catch (error) {
            console.error('Error canceling subscription:', error.message);
            alert('Failed to cancel the subscription. Please try again.');
        } finally {
            handleMenuClose();
        }
    };

    return (
        <Box sx={{ padding: 0 }}>
            {/* Header Row */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 6,
                    paddingLeft: '16px',
                    paddingRight: '16px',
                }}
            >
                {/* Left-aligned content */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditCardIcon sx={{ fontSize: '20px', color: '#2c3e50' }} />
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ color: '#2c3e50' }}
                    >
                        Subscriptions
                    </Typography>
                </Box>
            </Box>

            {/* Horizontal Menu */}
            <Box sx={{ px: 2 }}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                    }}
                >
                    <Tab label="Subscriptions" />
                    {/* <Tab label="Payment Methods" /> */}
                </Tabs>
                {/* Horizontal Line */}
                <Box sx={{ borderBottom: '1px solid #dcdcdc', width: '100%' }} />
            </Box>

            {/* Page Content */}
            <Box sx={{ px: 4, py: 3 }}>
                {value === 1 && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="body1" sx={{ marginBottom: 0 }}>
                            Methods
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddCard}
                            sx={{ alignSelf: 'center' }}
                        >
                            Add Card
                        </Button>
                    </Box>
                )}
                {value === 0 && (
                    <Box>
                        {/* Organizations Header */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 4,
                            }}
                        >
                            <Typography variant="body1" sx={{ marginBottom: 0 }}>
                                Each organization is managed under its own subscription.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    handleAddSubscription();
                                }}
                                sx={{ alignSelf: 'center' }}
                            >
                                Add Organization
                            </Button>
                        </Box>

                        {/* Subscriptions Content */}
                        {isLoading ? (
                            <LinearProgress
                                sx={{
                                    height: 4,
                                    borderRadius: 4,
                                    backgroundColor: '#e0e0e0', // Track color
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 4,
                                        backgroundColor: '#3f51b5', // Bar color
                                    },
                                }}
                            />
                        ) : error ? (
                            <Typography color="error" sx={{ textAlign: 'center' }}>
                                {error}
                            </Typography>
                        ) : (
                            subscriptions.map((subscription) => (
                                <Box
                                    key={subscription.subscriptionGuid}
                                    sx={{
                                        backgroundColor: '#e3f2fd',
                                        border: '1px solid #dcdcdc',
                                        borderRadius: '8px',
                                        padding: 3,
                                        marginBottom: 2,
                                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                        position: 'relative',
                                    }}
                                >
                                    {/* 3-dot Menu */}
                                    <IconButton
                                        aria-label="more"
                                        aria-controls="menu"
                                        aria-haspopup="true"
                                        onClick={(event) => handleMenuOpen(event, subscription.subscriptionGuid)}
                                        sx={{ position: 'absolute', top: 8, right: 8 }}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Menu
                                        id={`menu-${subscription.subscriptionGuid}`}
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl && activeSubscription === subscription.subscriptionGuid)}
                                        onClose={handleMenuClose}
                                        sx={{
                                            '& .MuiMenuItem-root': {
                                                color: '#2c3e50',
                                                fontWeight: 400,
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5',
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem
                                            onClick={() => {
                                                handleMenuClose();
                                                handleAddSubscription(subscription);
                                            }}
                                        >
                                            Upgrade Plan
                                        </MenuItem>
                                        <MenuItem onClick={handleRequestToCancel}>Request to Cancel</MenuItem>
                                        <MenuItem
                                            onClick={() => {
                                                handleMenuClose();
                                                navigate('/faq/subscription');
                                            }}
                                        >
                                            See FAQs
                                        </MenuItem>
                                    </Menu>

                                    {/* Subscription Details */}
                                    <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 400, marginBottom: 2 }}>
                                        <span style={{ fontWeight: 500 }}>Organization:</span> {subscription.orgName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 400, marginBottom: 2 }}>
                                        <span style={{ fontWeight: 500 }}>Plan:</span> {subscription.planId.toUpperCase()}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6c757d', marginBottom: 1 }}>
                                        <span style={{ fontWeight: 500 }}>Status:</span> {subscription.subscriptionStatus.toUpperCase()}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6c757d', marginBottom: 1 }}>
                                        <span style={{ fontWeight: 500 }}>Auto-Renew:</span> {subscription.autoRenewStatus ? 'Enabled' : 'Disabled'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6c757d', marginBottom: 1 }}>
                                        <span style={{ fontWeight: 500 }}>Subscriber Since:</span> {formatDate(subscription.purchasedAt)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6c757d', marginBottom: 1 }}>
                                        <span style={{ fontWeight: 500 }}>Renews on:</span> {formatDate(subscription.expiresAt)}
                                    </Typography>
                                </Box>
                            ))
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Billing;